import { useCallback, useEffect, useRef, useState } from 'react';
import { prizePool } from '@/mocks/site';

const levelStyle: Record<string, string> = {
  common: 'bg-background-100 text-foreground-800',
  rare: 'bg-accent-100 text-accent-900',
  epic: 'bg-primary-100 text-primary-800',
  none: 'bg-background-100 text-foreground-500',
};

export default function LuckyDraw() {
  const [active, setActive] = useState<number | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const spin = useCallback(() => {
    if (spinning) return;
    setSpinning(true);
    setResult(null);
    const target = Math.floor(Math.random() * prizePool.length);
    const totalSteps = prizePool.length * 3 + target;
    let step = 0;

    const tick = () => {
      step += 1;
      setActive(step % prizePool.length);
      if (step >= totalSteps) {
        setActive(target);
        setResult(prizePool[target].label);
        setHistory((prev) => [prizePool[target].label, ...prev].slice(0, 5));
        setSpinning(false);
        return;
      }
      const progress = step / totalSteps;
      const interval = 60 + progress * progress * 180;
      timerRef.current = window.setTimeout(tick, interval);
    };

    tick();
  }, [spinning]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8 items-start">
      <div className="lg:col-span-3 bg-background-50 border border-background-200 rounded-2xl p-4 md:p-6">
        <div className="grid grid-cols-3 gap-2 md:gap-3">
          {prizePool.map((prize, index) => {
            if (index === 4) {
              return (
                <button
                  key="draw-button"
                  type="button"
                  onClick={spin}
                  disabled={spinning}
                  className="aspect-square rounded-xl bg-primary-500 text-background-50 flex flex-col items-center justify-center gap-1 transition-colors hover:bg-primary-600 disabled:opacity-70 cursor-pointer"
                >
                  <i className="ri-trophy-line text-xl md:text-2xl"></i>
                  <span className="text-xs md:text-sm font-medium">{spinning ? '开奖中' : '开始抽奖'}</span>
                </button>
              );
            }
            const cellIndex = index > 4 ? index - 1 : index;
            const isActive = active === cellIndex && spinning;
            const isWinner = !spinning && result !== null && active === cellIndex;
            return (
              <div
                key={prize.label}
                className={`aspect-square rounded-xl border flex flex-col items-center justify-center gap-1.5 text-center px-2 transition-all duration-150 ${
                  isActive || isWinner
                    ? 'border-primary-400 bg-primary-50 ring-2 ring-primary-300'
                    : 'border-background-200 bg-background-100'
                }`}
              >
                <span className={`px-2 py-0.5 rounded-full text-[10px] ${levelStyle[prize.level] || levelStyle.common}`}>
                  {prize.level === 'epic' ? '稀有' : prize.level === 'rare' ? '精选' : prize.level === 'none' ? '参与' : '基础'}
                </span>
                <span className="text-xs md:text-sm text-foreground-900 leading-tight">{prize.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="lg:col-span-2 flex flex-col gap-4">
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
          <h3 className="text-base md:text-lg text-foreground-950">本次结果</h3>
          <div className="mt-4 min-h-[68px] flex items-center">
            {spinning ? (
              <p className="text-sm text-foreground-500 flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin text-primary-500 text-lg"></i>
                转盘高速运转中，请稍候…
              </p>
            ) : result ? (
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full bg-accent-100 text-accent-800 flex items-center justify-center text-xl">
                  <i className="ri-emotion-happy-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">恭喜抽中</p>
                  <p className="text-lg text-primary-700 font-medium">{result}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-foreground-500">点击中间的按钮开始演示，结果会显示在这里。</p>
            )}
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
          <h3 className="text-base md:text-lg text-foreground-950">演示记录</h3>
          {history.length === 0 ? (
            <p className="mt-3 text-sm text-foreground-500">还没有记录，先转一次试试手气。</p>
          ) : (
            <ul className="mt-3 flex flex-col gap-2">
              {history.map((item, idx) => (
                <li key={`${item}-${idx}`} className="flex items-center gap-2 text-sm text-foreground-700">
                  <span className="w-5 h-5 rounded-full bg-background-100 text-foreground-600 flex items-center justify-center text-[10px]">
                    {idx + 1}
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 pt-4 border-t border-background-200 text-xs text-foreground-500 leading-relaxed">
            此处为玩法演示，帮助理解转盘的运行方式与奖品层级，正式参与请前往福利活动中心。
          </p>
        </div>
      </div>
    </div>
  );
}